import { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

function pin(color, glyph) {
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;transform:translate(-50%,-100%)">
      <div style="width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};box-shadow:0 4px 10px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;border:2px solid #fff">
        <span style="transform:rotate(45deg);color:#fff;font-size:14px;font-weight:700">${glyph}</span>
      </div></div>`,
    iconSize: [30, 30],
    iconAnchor: [0, 0],
  });
}

const PICKUP = pin("#F26419", "A");
const DEST = pin("#D94F2B", "B");
const DRIVER = L.divIcon({
  className: "",
  html: `<div style="transform:translate(-50%,-50%)"><div style="width:22px;height:22px;border-radius:50%;background:#1A1A1A;border:3px solid #fff;box-shadow:0 0 0 6px rgba(17,24,20,.15)"></div></div>`,
  iconSize: [22, 22],
  iconAnchor: [0, 0],
});

// Badges véhicules : Moto=Jaune, Voiture=Bleu, Vélo=Vert. Pastille verte "Vérifié" si chauffeur conforme.
const VEHICLE = {
  moto: { bg: "#F5C518", stroke: "#1A1A1A", svg: '<circle cx="5.5" cy="17.5" r="3.5"/><circle cx="18.5" cy="17.5" r="3.5"/><path d="M15 6h5l-3 6"/><path d="M5.5 17.5 9 9h4l3 8.5"/>' },
  car: { bg: "#2563EB", stroke: "#fff", svg: '<path d="M19 17h2l-1.5-5.5a2 2 0 0 0-1.9-1.5H7.4a2 2 0 0 0-1.9 1.5L4 17h2"/><circle cx="7.5" cy="17.5" r="1.5"/><circle cx="16.5" cy="17.5" r="1.5"/><path d="M9 17h6"/>' },
  bike: { bg: "#16a34a", stroke: "#fff", svg: '<circle cx="5.5" cy="17.5" r="3.5"/><circle cx="18.5" cy="17.5" r="3.5"/><path d="M12 17.5V14l-3-3 4-3 2 3h2"/>' },
};

function vehicleIcon(type, verified) {
  const v = VEHICLE[type === "car" ? "car" : type === "bike" ? "bike" : "moto"];
  const check = verified
    ? `<span style="position:absolute;top:-4px;right:-4px;width:15px;height:15px;border-radius:50%;background:#16a34a;border:2px solid #fff;display:flex;align-items:center;justify-content:center">
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span>`
    : "";
  return L.divIcon({
    className: "",
    html: `<div style="transform:translate(-50%,-50%)"><div style="position:relative;width:32px;height:32px;border-radius:50%;background:${v.bg};border:2px solid #fff;box-shadow:0 3px 8px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center">
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="${v.stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${v.svg}</svg>${check}
    </div></div>`,
    iconSize: [32, 32],
    iconAnchor: [0, 0],
  });
}

const MOTO = vehicleIcon("moto", false);

const AGENT = L.divIcon({
  className: "",
  html: `<div style="transform:translate(-50%,-50%)"><div style="width:20px;height:20px;border-radius:50%;background:#2563EB;border:3px solid #fff;box-shadow:0 0 0 5px rgba(37,99,235,.22)"></div></div>`,
  iconSize: [20, 20],
  iconAnchor: [0, 0],
});

function ClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      if (onMapClick) onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function Fitter({ points }) {
  const map = useMap();
  useEffect(() => {
    const pts = points.filter(Boolean);
    if (pts.length === 0) return;
    if (pts.length === 1) {
      map.setView([pts[0].lat, pts[0].lng], 15);
    } else {
      map.fitBounds(pts.map((p) => [p.lat, p.lng]), { padding: [50, 50], maxZoom: 16 });
    }
  }, [JSON.stringify(points)]); // eslint-disable-line
  return null;
}

function AnimatedDriver({ position, icon }) {
  const [pos, setPos] = useState(position);
  const raf = useRef();
  const prev = useRef(position);
  useEffect(() => {
    if (!position) return;
    const start = prev.current || position;
    const t0 = performance.now();
    const dur = 900;
    const tick = (t) => {
      const k = Math.min(1, (t - t0) / dur);
      setPos({ lat: start.lat + (position.lat - start.lat) * k, lng: start.lng + (position.lng - start.lng) * k });
      if (k < 1) raf.current = requestAnimationFrame(tick);
      else prev.current = position;
    };
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [position && position.lat, position && position.lng]); // eslint-disable-line
  if (!pos) return null;
  return <Marker position={[pos.lat, pos.lng]} icon={icon} />;
}

export default function RideMap({ center, pickup, dest, driver, drivers, agents, onMapClick, height = 260 }) {
  const c = center || pickup || { lat: 3.848, lng: 11.502 };
  const line = [pickup, dest].filter(Boolean).map((p) => [p.lat, p.lng]);
  return (
    <div style={{ height, width: "100%", borderRadius: 20, overflow: "hidden" }} className="border border-[#E2DFD8]">
      <MapContainer center={[c.lat, c.lng]} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer
          attribution='&copy; OpenStreetMap, &copy; CARTO'
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          maxZoom={20}
        />
        <ClickHandler onMapClick={onMapClick} />
        <Fitter points={[pickup, dest, driver]} />
        {(drivers || []).map((d) => (
          <Marker key={d.id} position={[d.lat, d.lng]} icon={vehicleIcon(d.vehicle_type, d.verified)} />
        ))}
        {(agents || []).map((a) => (
          <Marker key={`ag-${a.id}`} position={[a.lat, a.lng]} icon={AGENT} />
        ))}
        {pickup && <Marker position={[pickup.lat, pickup.lng]} icon={PICKUP} />}
        {dest && <Marker position={[dest.lat, dest.lng]} icon={DEST} />}
        {driver && <AnimatedDriver position={driver} icon={MOTO} />}
        {line.length === 2 && <Polyline positions={line} pathOptions={{ color: "#D94F2B", weight: 4, dashArray: "8 8" }} />}
      </MapContainer>
    </div>
  );
}
